import { useEffect, useState } from 'react';
import { site, type BetaPageDto, type BetaPhase } from '../net/api';
import { Markdown } from './Markdown';

const SUPPORT = 'support@buddybay.net';

/** True on beta.buddybay.net (and beta-staging.buddybay.net), where every path shows the beta page. */
export const isBetaHost = () => /^beta(-staging)?\./.test(window.location.hostname);

/** The main game site, for links from the beta site. */
export function mainOrigin() {
  const { origin, hostname } = window.location;
  if (hostname.startsWith('beta-staging.')) return origin.replace('//beta-staging.', '//staging.');
  if (hostname.startsWith('beta.')) return origin.replace('//beta.', '//');
  return origin;
}

const PHASE_LABEL: Record<BetaPhase['status'], string> = { upcoming: 'Coming soon', open: 'Happening now', ended: 'Finished' };

function day(d: string) {
  const [y, m, dd] = d.split('-').map(Number) as [number, number, number];
  return new Date(y, m - 1, dd).toLocaleDateString(undefined, { dateStyle: 'long' });
}

function phaseDates(p: BetaPhase) {
  if (p.startDate && p.endDate) return `${day(p.startDate)} – ${day(p.endDate)}`;
  if (p.startDate) return `${p.status === 'upcoming' ? 'Starts' : 'Started'} ${day(p.startDate)}`;
  if (p.endDate) return `${p.status === 'ended' ? 'Ended' : 'Ends'} ${day(p.endDate)}`;
  return 'Dates to be announced';
}

/** beta.buddybay.net: who can join the beta, how to apply, and the beta phases. Edited by Administrators. */
export function BetaPage() {
  const [page, setPage] = useState<BetaPageDto>();
  const [error, setError] = useState<string>();
  const main = mainOrigin();
  useEffect(() => {
    site.betaPage().then((p) => { setPage(p); document.title = p.title; }, (e: Error) => setError(e.message));
  }, []);

  if (error || !page) {
    return <div className="center-screen"><p className={error ? 'form-error' : 'muted'}>{error ?? 'Loading…'}</p></div>;
  }
  if (!page.enabled) {
    return (
      <div className="center-screen beta-off">
        <section className="panel narrow">
          <img src="/brand/buddybay-logo.svg" alt="Buddy Bay" height={44} />
          <p>{page.disabledMessage || "This page isn't available right now."}</p>
          <a className="btn wide" href={main}>Go to Buddy Bay</a>
        </section>
      </div>
    );
  }
  const apply = page.applyLabel && page.applyUrl ? { label: page.applyLabel, href: page.applyUrl.startsWith('/') ? main + page.applyUrl : page.applyUrl } : null;

  return (
    <div className="landing-page beta-page">
      <header className="beta-hero">
        <nav className="landing-nav" aria-label="Beta page">
          <a href={main} aria-label="Buddy Bay home"><img src="/brand/buddybay-icon.svg" alt="" width={36} height={36} /></a>
          <span className="spacer" />
          {page.phases.length > 0 && <a href="#phases">Dates</a>}
          {page.eligibility.trim() && <a href="#eligibility">Who can join</a>}
          {page.howToApply.trim() && <a href="#apply">How to apply</a>}
          <a href={main}>Play Buddy Bay</a>
        </nav>
        <div className="beta-hero-body">
          <p className="eyebrow">Buddy Bay</p>
          <h1 className="brand big beta-title">{page.title}</h1>
          {page.tagline && <p className="tagline">{page.tagline}</p>}
          {apply && <a className="btn beta-apply" href={apply.href}>{apply.label}</a>}
        </div>
      </header>

      <div className="landing">
        {page.intro.trim() && (
          <section className="landing-section beta-prose">
            <Markdown source={page.intro} linkBase={main} />
          </section>
        )}

        {page.phases.length > 0 && (
          <section id="phases" className="landing-section">
            <div className="landing-head">
              <p className="eyebrow">Beta phases</p>
              <h2>Closed and open beta</h2>
            </div>
            <ol className="phase-list">
              {page.phases.map((p, i) => (
                <li key={i} className={`phase ${p.status}`}>
                  <div className="phase-top">
                    <h3>{p.name}</h3>
                    <span className={`phase-pill ${p.status}`}>{PHASE_LABEL[p.status]}</span>
                  </div>
                  <p className="phase-dates">{phaseDates(p)}</p>
                  {p.description.trim() && <Markdown source={p.description} linkBase={main} />}
                </li>
              ))}
            </ol>
          </section>
        )}

        {page.eligibility.trim() && (
          <section id="eligibility" className="landing-section">
            <div className="landing-head">
              <p className="eyebrow">Eligibility</p>
              <h2>Who can join the beta</h2>
            </div>
            <div className="beta-card"><Markdown source={page.eligibility} linkBase={main} /></div>
          </section>
        )}

        {page.howToApply.trim() && (
          <section id="apply" className="landing-section">
            <div className="landing-head">
              <p className="eyebrow">How to apply</p>
              <h2>Getting into the beta</h2>
            </div>
            <div className="beta-card">
              <Markdown source={page.howToApply} linkBase={main} />
              {apply && <a className="btn" href={apply.href}>{apply.label}</a>}
            </div>
          </section>
        )}

        {page.more.trim() && (
          <section className="landing-section beta-prose">
            <Markdown source={page.more} linkBase={main} />
          </section>
        )}

        <footer className="landing-footer">
          <a href={main}><img src="/brand/buddybay-logo.svg" alt="Buddy Bay" height={40} /></a>
          <nav className="policy-links" aria-label="Policies">
            <a href={`${main}/policies/rules`}>Community Rules</a>
            <a href={`${main}/policies/terms`}>Terms of Service</a>
            <a href={`${main}/policies/privacy`}>Privacy Policy</a>
            <a href={`${main}/policies/parents`}>Parent Guide</a>
          </nav>
          <p className="muted small-print">
            Questions about the beta? <a href={`mailto:${SUPPORT}`}>{SUPPORT}</a> · © {new Date().getFullYear()} Buddy Bay
          </p>
        </footer>
      </div>
    </div>
  );
}
